
import { useEffect, useRef } from "react";

interface FilterState {
  searchTerm: string;
  categoryId: number | null;
}

interface UseFilterTrackingProps {
  searchTerm: string;
  selectedCategoryId: number | null;
  refetch: () => Promise<void>;
}

export const useFilterTracking = ({ 
  searchTerm, 
  selectedCategoryId, 
  refetch 
}: UseFilterTrackingProps) => {
  const isInitialMount = useRef(true);
  
  const activeFiltersRef = useRef<FilterState>({
    searchTerm: "",
    categoryId: null
  });
  
  const lastAppliedFilters = useRef<FilterState>({
    searchTerm: "",
    categoryId: null
  });
  
  useEffect(() => {
    const searchChanged = searchTerm !== activeFiltersRef.current.searchTerm;
    const categoryChanged = selectedCategoryId !== activeFiltersRef.current.categoryId;
    
    if (searchChanged || categoryChanged) {
      console.log(`Filter change detected - Search: "${searchTerm}" (changed: ${searchChanged}), CategoryID: ${selectedCategoryId} (changed: ${categoryChanged})`);
      
      activeFiltersRef.current = {
        searchTerm,
        categoryId: selectedCategoryId
      };
      
      if (!isInitialMount.current) {
        console.log("Triggering immediate refetch due to filter change");
        
        // Use a shorter delay for category changes to improve responsiveness
        const delay = categoryChanged ? 50 : 100;
        setTimeout(() => {
          console.log(`Executing refetch after ${delay}ms delay`);
          refetch();
        }, delay);
      }
      
      lastAppliedFilters.current = {
        searchTerm,
        categoryId: selectedCategoryId
      };
    }
    
    isInitialMount.current = false;
  }, [searchTerm, selectedCategoryId, refetch]);

  return {
    activeFiltersRef,
    lastAppliedFilters,
    isInitialMount
  };
};
